// ================= THE AUTHORED HALF OF THE RULES PAGE =================
//
// WHAT THIS FILE IS FOR, AND — MORE IMPORTANTLY — WHAT IT IS NOT ALLOWED TO CONTAIN.
//
// rules.html renders from three sources. Two of them are data modules that already exist and
// already carry their own prose: js/mechanics-dictionary.js (every Attack Type, follow-through,
// Defense Type and range-fit verdict) and js/style-passives.js (the Foundation passives). The page
// RENDERS those; it must never restate them. That is the brief's rule, quoted in both of those
// files:
//
//     "The UI copy must come from one centralized mechanics dictionary. Do not reproduce the
//      definitions separately in card, tutorial, resolution, library, and glossary components."
//
// A glossary page is precisely the component that sentence was written about, so the rule binds
// hardest here. tests/rules-content-test.js enforces it as an assertion rather than an intention:
// no sentence in this file may also appear in a dictionary or passives entry.
//
// WHAT IS LEFT is the third source, and it is why this file exists: the systems that have no data
// module because no card carries them. Nothing in card data knows the order of an exchange, that
// Fury +2 grants Power, or that a bout is ten exchanges long — so those cannot be rendered from a
// table, and they are the only things authored here.
//
// AUTHORED FROM RULES.md, NEVER FROM MEMORY. RULES.md is "the rules as this engine actually runs
// them", and every section below compresses a numbered section of it. That instruction is not
// ceremonial: the plan that commissioned this file guessed that lower Speed resolves first, and
// §6 says the opposite in bold. Compress, do not copy, and check.
//
// AND CHECKED AGAINST THE ENGINE WHERE THE ENGINE CAN ANSWER. The Intent cycle below is a claim
// about behaviour, not about wording, so rules-content-test drives ENGINE.clashRangePreview
// through all six ordered pairs and holds the sentences to what the engine really does. That is
// the only honest way to author a rule the reader will act on: js/engine.js is the authority,
// this file is a description, and a description nobody checks becomes a lie on the day the
// authority moves.
//
// LENGTH IS A NUMBER, NOT A FEELING. The brief that governs the whole mechanics-visibility effort
// bans a rules wall outright ("Do not add a large rules wall to the landing page"). This page is
// not the landing page, so a reference is allowed to exist — but the ban's reasoning survives the
// move, and the test caps every `body` at BODY_MAX characters so "short authored sections" stays
// true under editing.
window.RulesContent = (function () {
  'use strict';

  // The cap the test enforces. Stated here rather than only in the test so an author hitting it
  // finds out why in the file they are editing.
  const BODY_MAX = 700;

  // `id`     — the anchor. rules.html#stamina is a real address, and the table of contents is
  //            built from these, so an id is a promise not to rename.
  // `title`  — the heading.
  // `body`   — two to five sentences of prose. Under BODY_MAX.
  // `points` — optional. One-line facts the body would have to bury in a subordinate clause.
  // `rulesSection` — the RULES.md heading this compresses. The test greps for it, so a section
  //            renumbered in RULES.md fails here rather than quietly pointing at nothing.
  const SECTIONS = [
    {
      id: 'exchange',
      title: 'One exchange, in order',
      rulesSection: '## 1. What a bout is',
      body: 'A bout is up to ten exchanges, and an exchange is one simultaneous clash rather than ' +
            'a pair of turns. Both fighters secretly commit the same three things — one card as ' +
            'their Attack, one as their Defense, and a Positioning Intent — and everything reveals ' +
            'at once. What follows is a fixed sequence, and knowing the order is most of knowing ' +
            'the game: the Intents contest first and settle where the fighting happens, then the ' +
            'attacks resolve in Speed order against that band, then the exchange settles somewhere ' +
            'and that becomes the next one\'s starting point.',
      points: [
        'Two fighters, 30-card decks, 15 Stamina each, five cards in hand.',
        'Exchange 1 begins at Pocket, the middle band.',
        'Hands refill to five at the end of every exchange.'
      ]
    },
    {
      id: 'range',
      title: 'Range: three bands, three answers',
      rulesSection: '## 3. Range',
      body: 'The fight stands in one of three bands — Long, Pocket, Clinch — and every exchange ' +
            'has three separate range values that it is worth refusing to conflate. The Start ' +
            'Range is where the exchange begins and is frozen for its whole length. The Clash ' +
            'Range is where the fighting actually happens, decided by the Intent contest, and it ' +
            'is the one your attack is measured against. The End Range is where the fighters ' +
            'finish, decided after both attacks resolve, and it becomes the next exchange\'s Start.',
      points: [
        'Start is frozen. Clash is what your attack is measured against. End becomes the next Start.',
        'A card prints the band or bands it wants; how far the Clash lands from those is the whole verdict.'
      ]
    },
    {
      id: 'intent',
      title: 'Positioning Intent',
      rulesSection: '## 4. Positioning Intent, and how Clash Range is decided',
      body: 'Every commit carries an Intent as well as two cards, chosen independently of which ' +
            'card attacks. All three are legal at every band. They form a closed three-way cycle ' +
            'resolved simultaneously, and winning it does not damage anybody — it decides where ' +
            'the exchange is fought, which is usually worth more. Picking the same Intent as your ' +
            'opponent is not a stalemate: the pair has a fixed result and nobody is said to have ' +
            'won it. Results are capped at the two ends; you cannot be pushed past Long or Clinch.',
      points: [
        'Pressure beats Anchor, and the Clash lands one band closer.',
        'Anchor beats Evade, and the Clash stays at the Start band.',
        'Evade beats Pressure, and the Clash lands one band farther.',
        'Matched Intents: two Pressures close, two Anchors hold, two Evades open.'
      ]
    },
    {
      id: 'speed',
      title: 'Speed decides who lands first',
      rulesSection: '## 6. Speed — who resolves first',
      body: 'Every attack card prints a Speed of one, two or three pips, and the higher one ' +
            'resolves first. Changes are applied and the result is then clamped to that one-to-three ' +
            'range, so a card already at three cannot be sped up and two penalties on a two leave ' +
            'it at one rather than nothing. Equal Speed is genuinely simultaneous rather than a ' +
            'coin flip: both attacks are computed from the same frozen state and neither sees the ' +
            'other\'s result, which is roughly a third of exchanges and therefore a main line.',
      points: [
        'Speed belongs to the Attack card alone.',
        'A Defense that earns you tempo earns it for your next attack, never the one beside it.'
      ]
    },
    {
      id: 'power',
      title: 'Power, Guard and what a defense covers',
      rulesSection: '## 7. Resolving an attack',
      body: 'An attack carries a Power and aims at one or more levels — High, Mid, Low. A defense ' +
            'carries a Guard number and covers a set of levels, and which of those two facts ' +
            'matters depends entirely on whether the level you aimed at was covered. Aim where ' +
            'they are not covering and Guard never enters the arithmetic at all. Aim where they ' +
            'are and Guard is subtracted. This is why the banded figure is drawn on every card ' +
            'surface in the game: it is the same question asked in a picture.',
      points: [
        'Uncovered level: damage is Power plus one, and the Guard number is not consulted.',
        'Covered level: damage is Power minus Guard, never below zero.',
        'Stopping the attack dead is not automatically a Full Guard: the four Defense Family results need the attack to have had real Power to stop.',
        'A card whose own text says "on a full guard" uses the looser test, and fires whenever a covered attack dealt no damage.'
      ]
    },
    {
      id: 'openings',
      title: 'Openings',
      rulesSection: '## 8. Openings',
      body: 'An Opening is a temporary vulnerability sitting on a fighter at one specific level and ' +
            'carrying a bonus, and an attack aimed at that level adds the bonus to its Power. A ' +
            'level holds at most one at a time: a second arrival keeps whichever bonus is larger ' +
            'and refreshes how long it lasts, so a small one landing later can never downgrade a ' +
            'big one already there. They are short-lived by design — an Opening survives exactly ' +
            'one exchange past the one that made it, then expires unspent.',
      points: [
        'Openings carry a type — General, Combination or Recovery.',
        'As of this build the type does not change what any attack may spend it with.'
      ]
    },
    {
      id: 'stamina',
      title: 'Stamina, cost and recovery',
      rulesSection: '## 13. Stamina, cost and recovery',
      body: 'Stamina is the only life total and the only clock that moves every exchange. Both ' +
            'halves of a card may print a cost, and the two are paid at different moments for a ' +
            'reason worth internalising: a Defense is brought to the whole exchange rather than ' +
            'spent at a moment in it, so its cost is paid up front whether or not anybody attacks ' +
            'into it. An Attack pays when it resolves. Recovery is budgeted rather than unlimited, ' +
            'so stacking three sources of it in one exchange does not stack three times.',
      points: [
        'Fifteen Stamina, and reaching zero ends the bout immediately.',
        'All recovery shares a cap of two Stamina per fighter per exchange.',
        'Drawing from an empty deck reshuffles your discard and costs one Stamina.'
      ]
    },
    {
      id: 'furyflow',
      title: 'The Fury–Flow track',
      rulesSection: '## 12. The Fury–Flow track',
      body: 'One track runs from minus three to plus three and starts at zero, with Flow at the ' +
            'negative end and Fury at the positive. It is the game\'s only persistent mindset ' +
            'state, and each end pays differently: deep Flow gives Stamina back while you are ' +
            'not being hit, high Fury gives Power whatever happens. Being at high Fury does not expose you — it grants its bonus unconditionally ' +
            'and creates no vulnerability of its own, which an older rule got wrong and which was ' +
            'deleted rather than tuned.',
      points: [
        'Flow at minus two or lower: recover one Stamina during Aftermath, but only if their ' +
          'Attack did not damage you this exchange.',
        'Fury at plus two or higher: plus one Power to your attack, unconditionally.',
        'The two ends are deliberately not symmetrical: Fury always pays, Flow pays only while ' +
          'you are not being hit. Hitting a fighter who is sitting in Flow is what takes it away.'
      ]
    },
    {
      id: 'offbalance',
      title: 'Off-Balance',
      rulesSection: '## 14. Off-Balance',
      body: 'Off-Balance is a flag rather than a number: a fighter either is or is not, and it ' +
            'lasts exactly one exchange — set during one, it clears at the end of the next. On its ' +
            'own it does nothing at all. It matters because other things read it, so its value is ' +
            'entirely in what you are holding when your opponent has it.',
      points: []
    },
    {
      id: 'winning',
      title: 'Winning',
      rulesSection: '## 15. Winning',
      body: 'There are two ways a bout ends and they are not equally common. A stoppage happens ' +
            'the moment a fighter reaches zero Stamina, and if both reach it in the same exchange ' +
            'the bout is a draw rather than a win for whoever resolved first. Otherwise ten ' +
            'exchanges pass with both standing and it goes to a decision, which reads Stamina ' +
            'first, then clean hits landed, and calls a complete draw if even those are level.',
      points: []
    },
    {
      id: 'decks',
      title: 'Building a deck',
      rulesSection: '## 17. Deck construction',
      body: 'Every deck is thirty cards: at least twelve from one of the eight named Styles, at ' +
            'least eight from that Style\'s Foundation, at most two copies of any card, and the ' +
            'rest as you like. The shipped decks follow exactly that rule rather than a stricter ' +
            'one of their own — load one in the builder and it validates like anything you would ' +
            'build, which is the point of shipping examples at all. Each Style belongs to one of ' +
            'four Foundations, and the Foundation is what a passive keys off. The builder enforces ' +
            'all of it as you go, which is a better place to learn it than a list.',
      points: []
    }
  ];

  function all() { return SECTIONS.slice(); }
  function get(id) {
    for (let i = 0; i < SECTIONS.length; i++) if (SECTIONS[i].id === id) return SECTIONS[i];
    return null;
  }

  // ---------------- The kind headings for the rendered glossary ----------------
  //
  // The dictionary groups its entries under four KINDS and names them internally
  // ('attackType', 'followThrough', 'defenseType', 'rangeFit'); what it does not carry is a
  // player-facing heading and a sentence saying what the whole family is for, because no card
  // needs one. That is authored, so it lives here — and it is the boundary this file is policing:
  // one line ABOUT the family, never a restatement of any member.
  const KIND_HEADINGS = {
    attackType: {
      title: 'Attack Types',
      blurb: 'What kind of strike a card is. Conditional card text and Style passives match on ' +
             'this, so it is the label most rules care about.'
    },
    followThrough: {
      title: 'Follow-throughs',
      blurb: 'What an attack sets up when it gets through. Most of a card\'s long-term value is ' +
             'here rather than in its Power.'
    },
    defenseType: {
      title: 'Defense Types',
      blurb: 'What a defense does when it stops everything. Guard says how much it stops; this ' +
             'says what happens next, and three of the four need a Full Guard to do anything.'
    },
    rangeFit: {
      title: 'Range fit',
      blurb: 'How far the Clash landed from the band your card wanted, and what that costs you.'
    },
    track: {
      title: 'The Fury–Flow track',
      blurb: 'The one number that persists between exchanges, and what each end of it pays for.'
    },
    chamber: {
      title: 'Chamber',
      // The blurb must not name a member of any dictionary table, which is why this one says
      // "a handful of Foundation cards" rather than naming the Foundation they belong to: that
      // Foundation is Kinetic Impact, and IMPACT is an entry label.
      blurb: 'A resource a handful of Foundation cards can store, spend, or take away. Every ' +
             'other mechanic on this page is in every bout; this one is in a bout only because ' +
             'somebody built a deck that carries it.'
    },
    fatigue: {
      title: 'Running out of deck',
      // The only family on this page that nobody chooses and no card causes -- which is the one
      // sentence worth spending the blurb on, because every reader arrives at it assuming
      // something did this to them.
      blurb: 'The clock that is not the ten-exchange limit. Nothing triggers it and nobody ' +
             'inflicts it; you simply reach the bottom of your own deck.'
    },
    cycle: {
      title: 'Cycling cards away',
      // Named as the pair it is with the section directly above: this is the thing you choose to
      // do and that is what it eventually costs. A reader who arrives at one should be told the
      // other exists.
      blurb: 'The one way to dig for a card instead of waiting for it — and the fastest way to ' +
             'reach the bottom of your deck, which is the section above this one.'
    },
    intent: {
      title: 'Positioning Intent',
      // NAMES NONE OF ITS THREE MEMBERS, which is the rule every heading here follows and which
      // bites hardest on this one: the three are the most quotable words in the game. The entries
      // below the heading are where they are named and compared; this says what the family is
      // FOR, and what makes it unlike everything else on the page — it is the only thing here
      // that two people decide about each other at the same moment, without knowing.
      //
      // It renders LAST because js/rules-ui.js walks MechanicsDictionary.KINDS and this kind is
      // appended there. That ordering is about lookup precedence rather than teaching order, and
      // reshuffling KINDS to move a glossary section up the page would move findAnywhere's
      // resolution for every key that came before it — too much to spend on placement in a
      // reference the reader arrives at by nav link.
      blurb: 'The only decision both fighters make at the same time, each blind to the other. ' +
             'It settles nothing about damage and everything about where the exchange is ' +
             'fought — which is the distance every card on the table is then judged against.'
    }
  };

  return { SECTIONS, all, get, KIND_HEADINGS, BODY_MAX };
})();
